import axiosInstance from "./api";
import { getCoreModels } from "./coreModelService";
import { getIdToken } from "./auth";
import { API_URL } from "./config";

class AssignmentPersona {
  persona;
  predates_versions;
  latest_version;
}

const personaService = {
  /**
   * Get all personas, sorted alphabetically by name
   * @returns {Promise<*|*[]>}
   */
  async getAll() {
    const result = await axiosInstance.get("/api/persona/");
    const personas = result?.data?.data?.entries || [];
    return personas.sort((a, b) => a.name.localeCompare(b.name));
  },

  /**
   * Get a single persona by ID
   * @param {string} id - The personas UUID.
   * @param {Object} [options]
   * @param {boolean} [options.deletedOk] - Also resolve a soft-deleted persona.
   * @returns {Promise<*>}
   */
  async getLatest(id, { deletedOk = false } = {}) {
    const result = await axiosInstance.get(`/api/persona/${id}`, {
      params: deletedOk ? { deleted_ok: true } : undefined,
    });
    return result?.data?.data;
  },

  /**
   * Get the image URL for a persona, which includes an authentication token for access
   * @param {string} personaId - the persona's UUID.
   * @param {number} [version] - the persona's version, used to bust the browser's cache
   *   when the image is replaced (the image endpoint is served as immutable/cacheable).
   * @returns {Promise<string>}
   */
  async getImageUrl(personaId, version) {
    const params = new URLSearchParams({ token: await getIdToken() });
    if (version !== undefined && version !== null) {
      params.set("version", version);
    }
    return `${API_URL}api/persona/image/${personaId}?${params.toString()}`;
  },

  /**
   * Get the persona given an assignment ID.
   * @param {string} assignmentId - The assignment whose persona you wish to retrieve
   * @returns {Promise<AssignmentPersona>}
   */
  async getPersonaByAssignmentId(assignmentId) {
    const response = await axiosInstance.get(
      `/api/persona/by-assignment/${assignmentId}`,
    );
    return response?.data?.data || {};
  },

  /**
   * Upload an image file for a persona. The file should be a valid image format (e.g., JPEG, PNG).
   * @param {File} file - the image file.
   * @param {string} personaId - the UUID of the persona this image should be attached to.
   * @returns {Promise<any>}
   */
  async uploadImage(file, personaId) {
    const formData = new FormData();
    formData.append("file", file);
    const response = await axiosInstance.post(
      `/api/persona/image/${personaId}`,
      formData,
      { headers: {} },
    );
    return response.data;
  },

  /**
   * Delete the currently stored image for a persona.
   * @param {string} personaId - the UUID of the persona whose image should be removed.
   * @returns {Promise<any>}
   */
  async deleteImage(personaId) {
    const response = await axiosInstance.delete(
      `/api/persona/image/${personaId}`,
    );
    return response.data;
  },

  /**
   * Create a new persona.
   * @param param0
   * @param param0.name
   * @param param0.gender
   * @param param0.stt
   * @param param0.generative
   * @param param0.tts
   * @param param0.phone_number
   * @param param0.sms_supported
   * @param param0.voice_supported
   * @param param0.tags
   * @param param0.pipecat_enabled
   * @param param0.external_voice_bot_id
   * @param param0.external_voice_gateway_id
   * @returns {Promise<*>}
   */
  async create({
    name,
    gender,
    stt = null,
    generative = null,
    tts = null,
    phone_number = null,
    sms_supported = true,
    voice_supported = true,
    tags = [gender],
    pipecat_enabled = false,
    external_voice_bot_id = null,
    external_voice_gateway_id = null,
  }) {
    if (!stt || !generative || !tts) {
      // assign default models
      const models = await getCoreModels();
      if (!stt) {
        stt =
          models.find((m) => m.type === "stt" && m.provider === "google")?.id ||
          null;
      }
      if (!generative) {
        generative =
          models.find(
            (m) =>
              m.type === "generative" &&
              m.id === "google-gemini-2.5-flash-nonthinking",
          )?.id || null;
      }
      if (!tts) {
        tts =
          models.find((m) => m.type === "tts" && m.provider === "google")?.id ||
          null;
      }
    }

    if (!phone_number) {
      phone_number = `+12223334444`;
    }

    const result = await axiosInstance.post("/api/persona/", {
      name,
      description:
        "This is a new persona; be sure to update the description, phone number, and other settings before using.",
      models: {
        voice: { stt, generative, tts },
        sms: { generative },
        preprocessing: { generative },
      },
      channel: { phone_number, sms_supported, voice_supported },
      tags,
      metadata: {},
      pipecat_enabled: pipecat_enabled, // default to false
      external_voice_bot_id,
      external_voice_gateway_id,
    });
    return result?.data?.data;
  },

  /**
   * Update an existing persona by ID. Only the fields included in personaData will be updated.
   * @param id
   * @param personaData
   * @returns {Promise<axios.AxiosResponse<any>>}
   */
  update(id, personaData) {
    return axiosInstance.put(`/api/persona/${id}`, personaData);
  },

  /**
   * Soft-delete a persona. Existing assignments and sessions keep resolving it.
   * @param {string} id - The persona's UUID.
   * @returns {Promise<void>}
   */
  async remove(id) {
    await axiosInstance.delete(`/api/persona/${id}`);
  },

  /**
   * Restore a soft-deleted persona.
   * @param {string} id - The persona's UUID.
   * @returns {Promise<*>} the restored persona
   */
  async recover(id) {
    const result = await axiosInstance.post(`/api/persona/${id}/recover`);
    return result?.data?.data;
  },

  /**
   * Get every soft-deleted persona, newest deletion first.
   * @returns {Promise<*[]>}
   */
  async getDeleted() {
    const result = await axiosInstance.get("/api/persona/deleted");
    const personas = result?.data?.data || [];
    return personas.sort(
      (a, b) => new Date(b.deleted_at) - new Date(a.deleted_at),
    );
  },
};

export default personaService;
